import { useEffect, useState } from 'react';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { AssetLinks } from '@assets/assets';
import { useFormContext } from '@context/FormContext';
import { TextInput } from '@components/shared/form/TextInput';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { AddressInfo, CDX_ACTIONS } from 'types/form';
import { addressTypeOptions } from '@shared/constants/addressTypes';
import useForm, { validate } from '@shared/hooks/useForm';
import { Screens } from '@shared/constants/screens';
import { PriceInput } from '@components/shared/form/PriceTextInput';
import { monthsBetween, todaysDate } from 'utils/dateUtils';
import { getContactData } from '@services/utils';
import { createAddressHistory } from '@services/addressHistory';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { convertArrayToSnakeCase } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

type ICurrentAddressInfo = Pick<AddressInfo, 'type' | 'monthlyPayment'> & { moveInDate: string };

export interface IOriginalAddressInfo {
  address: string;
  city: string;
  extendedAddress: string | null;
  postalCode: string;
  region: string;
}

export const formAddressFormatResponse = (addressResponse: AddressInfo[]) => {
  return addressResponse.map((addr: AddressInfo) => formatAddress(addr));
};

export const formatAddress = (address: AddressInfo) => {
  return {
    streetLine1: address?.streetLine1 ?? '',
    streetLine2: address?.streetLine2 ?? '',
    city: address?.city,
    state: address?.state,
    zip: address?.zip,
    moveInDate: '',
    moveOutDate: '',
    type: '',
    monthlyPayment: 0,
  };
};

const CurrentAddressInfoForm = () => {
  const { t } = useTexts();
  const { next } = useScreenNavigator();

  const { trackFormSubmit, trackPage, startInteraction, trackClick } = useAnalytics({
    ...SEGMENT_SCREEN_PARAMS.ADDRESS_LOOP,
    field_count: 8,
    required_field_count: 6,
  });

  const [contactAdddressResponse, setcontactAdddressResponse] = useState<AddressInfo[] | []>([]);
  const { currentStep, handleStep, setAddressHistory, dispatch, isLoading, submitStart, submitSuccess } = useFormContext();
  const contactData = getContactData();

  const addressInfo = {
    moveInDate: '',
    type: '',
    monthlyPayment: 0,
  };

  const { errors, handleChange, handleSubmit, values ,setValues} = useForm<ICurrentAddressInfo>({
    initialValues: {
      ...addressInfo,
    },
    validationSchema: {
      moveInDate: validate.string('moveInDate').required(t('addressHistory.formValidation.moveInDate')),
      type: validate.string('type').required(t('addressHistory.formValidation.residentType')),
      monthlyPayment: validate.number('monthlyPayment'),
    },

    onSubmit: async (values) => {
      const { moveInDate } = values;
      const months = monthsBetween(moveInDate, new Date());
      const formatAddressResponse = formAddressFormatResponse(contactAdddressResponse);
      const trackingFormattedAddressResponse = formatAddressResponse.map(({ zip, state, city }) => ({
        zip,
        state,
        city,
      }));
      if (formAddressFormatResponse?.length > 0) {
        formatAddressResponse[0].moveInDate = values?.moveInDate;
        formatAddressResponse[0].moveOutDate = new Date().toISOString().split('T')[0];
        formatAddressResponse[0].type = values?.type;
        formatAddressResponse[0].monthlyPayment = values?.monthlyPayment;
        setAddressHistory(formatAddressResponse);
        if (months < 24) {
          trackFormSubmit('Address Information Provided', {
            completed_status: false,
            submission_method: 'select',
            addresses_provided_count: 1,
            total_months_provided: months,
            months_at_address: months,
            months_of_history_required: 24,
            months_of_history_remaining: Math.abs(24 - months),
            form_context: 'address_loop',
            ...trackingFormattedAddressResponse,
          });
        } else {
          submitStart();

          const createAddressHistoryResponse = await createAddressHistory(formatAddressResponse, contactData?.contactId);

          if ('error' in createAddressHistoryResponse) {
            dispatch({
              type: CDX_ACTIONS.SUBMIT_API_ERROR,
              payload: createAddressHistoryResponse,
            });
          } else {
            trackFormSubmit('Address Information Provided', {
              completed_status: true,
              submission_method: 'select',
              addresses_provided_count: 1,
              total_months_provided: months,
              months_at_address: months,
              months_of_history_required: 24,
              months_of_history_remaining: Math.abs(24 - months),
              form_context: 'address_loop',
              ...trackingFormattedAddressResponse,
            });
            trackFormSubmit('Form Submitted', {
              submission_status: true,
              submission_method: 'select',
              addresses_provided_count: 1,
              months_of_history_captured: months,
              months_of_history_required: 24,
              months_of_history_remaining: Math.abs(24 - months),
              forward_screen_name: 'address_loop',
            });
            submitSuccess();
          }
        }
        next(currentStep, 'postSubmitOutcome', { monthsAtAddress: months }, months < 24 ? false : true);
      }
    },
    onValidationError: (errors, values) => {
      const { moveInDate } = values;
      const months = monthsBetween(moveInDate, new Date());
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        submission_method: 'select',
        addresses_provided_count: 1,
        months_of_history_captured: months,
        months_of_history_required: 24,
        months_of_history_remaining: Math.abs(24 - months),
        forward_screen_name: 'address_loop',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  useEffect(() => {
    if (contactData) {
      let updatedAddress = [];
      if (Array.isArray(contactData?.addresses) && contactData?.addresses?.length > 0) {
        updatedAddress = contactData?.addresses;
        setcontactAdddressResponse(updatedAddress);
        if (contactData?.leadSourceId) {
          const addressFromResponse = {
            moveInDate: '',
            type: updatedAddress[0]?.type || '',
            monthlyPayment: updatedAddress[0]?.monthlyPayment || 0,
          };
          setValues(addressFromResponse);
        }
      } else {
        next(currentStep, 'onLoad', { addressFound: contactData?.addresses?.length }, true);
      }
    }
  }, []);

  const handleInputChange = <K extends keyof ICurrentAddressInfo>(field: K, value: ICurrentAddressInfo[K]) => {
    handleChange(field, value);
  };

  const handleNotMyAddress = () => {
    trackClick('CTA Clicked', {
      cta_id: 'not_my_current_address',
      cta_text: 'This is not my current address',
      cta_type: 'link',
      cta_context: 'address_confirmation',
    });
    handleStep(Screens.CURRENT_ADDRESS_INFO_MANUALLY);
  };

  useEffectOnce(() => trackPage(t('pageTitles.AddressHistory')));

  return (
    <div className="iLendingCDX-full-width">
      <h1 className="iLendingCDX-form-title ">{t('currentAddress.title')}</h1>
      {contactAdddressResponse?.[0] && (
        <div className="iLendingCDX-verifyAddressCard iLendingCDX-mb-4">
          <div className="iLendingCDX-verifyAddressCard-homeIcon ">
            <img
              src={AssetLinks.home}
              alt="home icon"
            />
          </div>

          <div className="iLendingCDX-verifyAddressCard-info">
            <p>{contactAdddressResponse[0]?.streetLine1} </p>
            <p>{contactAdddressResponse[0]?.streetLine2}</p>
            <p>
              {contactAdddressResponse[0]?.city}, {contactAdddressResponse[0]?.state} {contactAdddressResponse[0]?.zip}
            </p>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        onFocus={startInteraction}
      >
        <TextInput
          label={t('currentAddress.form.moveInDate')}
          className={'iLendingCDX-mb-2'}
          name="moveInDate"
          type="date"
          errorMessage={errors.moveInDate}
          max={todaysDate}
          onChange={(e) => handleInputChange('moveInDate', e.target.value)}
        />
        <CustomSelect
          label={t('currentAddress.form.residentType')}
          placeholder={t('currentAddress.form.residentType')}
          className={'iLendingCDX-mb-4'}
          name="residentType"
          options={addressTypeOptions}
          value={values?.type}
          errorMessage={errors.type}
          onChange={(e) => handleInputChange('type', e.target.value)}
        />

        <PriceInput
          label={t('currentAddress.form.monthlyPaymentAmount')}
          className={'iLendingCDX-mb-2'}
          name="monthlyPaymentAmount"
          value={values?.monthlyPayment}
          errorMessage={errors.monthlyPayment}
          onChange={(value) => handleInputChange('monthlyPayment', value as ICurrentAddressInfo['monthlyPayment'])}
        />

        <Button
          variant="ghost"
          size="lg"
          className={'iLendingCDX-mb-4'}
          onClick={handleNotMyAddress}
        >
          {t('currentAddress.thisIsNotMyAddress')}
        </Button>

        <Button
          type="submit"
          className="iLendingCDX-button--rounded"
          size="lg"
          isLoading={isLoading}
        >
          {t('enterLicensePlate.form.continue')}
        </Button>
      </form>
    </div>
  );
};
export default CurrentAddressInfoForm;
